import { useEffect, useState } from 'react';

import { Button } from '@/components/ui';
import { logTrackedError } from '@/errorTracking';
import { confirmDialog } from '@/services/DialogService';
import { humanFileSize } from '@/utils/numerical';

const LOCAL_STORAGE_WARNING_THRESHOLD = 2 * 1024 * 1024;

export const getLocalStorageStats = () => {
  const keysAndSizes = Object.entries(localStorage).map(([key, value]) => ({
    key,
    size: value.length,
  }));
  keysAndSizes.sort((a, b) => b.size - a.size);
  const topKeysMsg = keysAndSizes
    .slice(0, 5)
    .map(k => `${k.key}: ${humanFileSize(k.size)}`)
    .join('; ');
  return {
    keysAndSizes,
    topKeysMsg,
    total: keysAndSizes.reduce((acc, { size }) => acc + size, 0),
  };
};

export const LocalStoragePopup = () => {
  const [showMessage, setShowMessage] = useState(false);

  const checkLocalStorageUsage = () => {
    const localStorageSize = getLocalStorageStats().total;
    if (localStorageSize > LOCAL_STORAGE_WARNING_THRESHOLD) {
      setShowMessage(true);
      logTrackedError({
        sourceName: 'LocalStoragePopup',
        origin: new Error(),
        stackError: new Error(),
        context: {
          localStorageSize: humanFileSize(localStorageSize),
          topKeys: getLocalStorageStats().topKeysMsg,
        },
        userMessage: `Local storage size exceeded 1MB.`,
        skipWindowAlert: true,
      });
    } else {
      setShowMessage(false);
    }
  };

  useEffect(() => {
    checkLocalStorageUsage();
    const checkLocalStorageInterval = setInterval(checkLocalStorageUsage, 5 * 60 * 60);

    return () => clearInterval(checkLocalStorageInterval);
  }, []);

  if (!showMessage) {
    return null;
  }

  return (
    <div
      role="alert"
      className="fixed bottom-4 right-4 z-notice max-w-notice rounded-auditor-lg border border-critical bg-surface p-3 text-sm text-ink shadow-auditor-overlay"
    >
      <p className="m-0 font-bold">
        PLEASE DISABLE YOUR ADBLOCKER
        <br />
        <br />
      </p>
      <p className="m-0">
        More local storage is being used than expected, which could be caused by an adblocker. <br />
        <br />
        We use usage telemetry to improve the Auditor's features and performance. It goes to our own API now rather than
        a third-party analytics host, so an adblocker is much less likely to block it, but the storage growth above can
        still slow the Auditor down.
        <br />
        <br />
        If you have disabled adblockers and are still recieving this warning, please reach out to IT Support so they can
        get to the bottom of it.
        <br />
        <br />
        For debugging purposes:
        <br />
        {getLocalStorageStats().topKeysMsg}
      </p>
      <br />
      <div className="flex gap-2">
        <Button
          className="h-auto min-h-9 flex-1 py-2"
          onClick={async () => {
            if (!(await confirmDialog('Are you sure you want to clear local storage?'))) {
              return;
            }
            setShowMessage(false);
            localStorage.clear();
            window.location.reload();
          }}
        >
          Clear local storage (this will logout and refresh)
        </Button>
        <Button
          className="flex-1"
          variant="danger"
          onClick={() => {
            setShowMessage(false);
          }}
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
};
